import React, { useState, useEffect, useRef, useCallback } from "react";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Grid,
  Card,
  Box,
  Button,
  Typography,
  CardMedia,
  CardContent,
  Dialog,
  IconButton,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getCurrentDateYYYYMM, fetchCam } from "../Hooks/Api";
import { TokenAtom, TokenInfo } from "../Hooks/TokenAtom";
import { downloadFile } from "../downloadFile";
import { useRecoilValue } from "recoil";
import { formatDateDialog } from "./Home";
import UploadButtonCam from "./UploadButtonCam";
import CamCorderDialog from "./CamCorderDialog";
import MySnackbar from "../Hooks/MySnackBar";

import { createTheme, ThemeProvider } from "@mui/material/styles";

// 렌더링마다 재생성되지 않도록 컴포넌트 외부에 정의
const pickerThemeCam = createTheme({
  palette: {
    primary: { main: "#8c6744" },
    secondary: { main: "#cf9e62" },
  },
  typography: {
    fontFamily: "LeeSeoyun, sans-serif",
  },
});

const formatDateCamCorder = (dateString) => {
  if (!dateString) return "";
  const [year] = dateString.split("-");
  return `${year}년`;
};

const Camcorder = () => {
  const [value, setValue] = useState(getCurrentDateYYYYMM());
  const [isLatest, setIsLatest] = useState(true); // 최신순/과거순 상태
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [sortedData, setSortedData] = useState([]); // 정렬된 데이터 상태
  const [snackBarSeverity, setSnackBarSeverity] = useState("success"); // 메시지 유형 설정
  const [currentId, setCurrentId] = useState(null); // 현재 선택된 id 저장
  const [selectedGroup, setSelectedGroup] = useState([]); // 현재 그룹 배열
  const [selectedIndex, setSelectedIndex] = useState(0); // 그룹 내 현재 인덱스
  const [slideDirection, setSlideDirection] = useState("left"); // 슬라이드 방향
  const [animKey, setAnimKey] = useState(0); // 애니메이션 트리거 키
  const [detailSearchOpen, setDetailSearchOpen] = useState(false);
  const tokens = useRecoilValue(TokenAtom);
  const tokenInfo = useRecoilValue(TokenInfo);
  const queryClient = useQueryClient();
  const specificDate = dayjs("2022-07-07"); // 특정 날짜를 생성
  const inputRef = useRef();

  const {
    data: itemData,
    isLoading,
    isError,
  } = useQuery(["photoCamCorders", value], () => fetchCam(value, tokens), {
    staleTime: 300000, //photoCamCorders 캐시된 데이터의 유효 시간 설정 (예: 5분)
    cacheTime: 900000, // 캐시된 데이터를 유지하는 시간 설정 (예: 15분)
  });

  const mutation = useMutation((date) => fetchCam(date, tokens), {
    onSuccess: (data) => {
      queryClient.setQueryData(["photoCamCorders", value], data);
      queryClient.invalidateQueries(["photoCamCorders", value]); // 쿼리 무효화
    },
  });

  // itemData가 변경될 때마다 정렬 적용
  useEffect(() => {
    if (itemData) {
      setSortedData(
        [...itemData].sort(
          (a, b) =>
            isLatest
              ? new Date(b.date) - new Date(a.date) // 최신순
              : new Date(a.date) - new Date(b.date) // 과거순
        )
      );
    }
  }, [itemData, isLatest]);

  useEffect(() => {
    if (currentId && itemData) {
      const updatedVideo = itemData.find((video) => video.id === currentId);
      if (updatedVideo) {
        setSelectedVideo(updatedVideo); // 변경된 video를 상태에 저장
      }
    }
  }, [itemData, currentId]); // itemData 또는 currentId가 변경될 때마다 실행

  const handleResetClick = () => {
    const newDate = getCurrentDateYYYYMM();
    setValue(newDate);
    mutation.mutate(newDate);
  };
  const handleNewClick = () => {
    setValue(specificDate);
    mutation.mutate(specificDate);
  };
  const handleToggleClick = () => {
    setIsLatest((prevIsLatest) => !prevIsLatest); // 상태 토글
  };

  const handleVideoClick = useCallback((video) => {
    const idx = sortedData.findIndex((v) => v.id === video.id);
    setCurrentId(video.id);
    setSelectedVideo(video);
    setSelectedGroup([...sortedData]);
    setSelectedIndex(idx);
    setDialogOpen(true);
  }, [sortedData]);

  const handlePrev = useCallback(() => {
    setSlideDirection("right");
    setAnimKey((k) => k + 1);
    setSelectedIndex((prev) => {
      const next = prev - 1;
      const video = selectedGroup[next];
      setSelectedVideo(video);
      setCurrentId(video.id);
      return next;
    });
  }, [selectedGroup]);

  const handleNext = useCallback(() => {
    setSlideDirection("left");
    setAnimKey((k) => k + 1);
    setSelectedIndex((prev) => {
      const next = prev + 1;
      const video = selectedGroup[next];
      setSelectedVideo(video);
      setCurrentId(video.id);
      return next;
    });
  }, [selectedGroup]);

  const handleJump = useCallback((i) => {
    const dir = i > selectedIndex ? "left" : "right";
    setSlideDirection(dir);
    setAnimKey((k) => k + 1);
    setSelectedIndex(i);
    setSelectedVideo(selectedGroup[i]);
    setCurrentId(selectedGroup[i].id);
  }, [selectedGroup, selectedIndex]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedVideo(null);
    setCurrentId(null);
    setSelectedGroup([]);
    setSelectedIndex(0);
  };
  const handleDetailClickOpen = () => {
    setDetailSearchOpen(true);
  };

  const handleDetailClose = () => {
    setDetailSearchOpen(false);
  };
  const handleDetailSearch = () => {
    const keyword = inputRef.current.value;
    setValue(keyword);
    mutation.mutate(keyword);
    handleDetailClose();
  };

  const handleUploadSuccess = (status) => {
    if (status === 201) {
      setSnackBarMessage("업로드 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoCamCorders", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 414) {
      setSnackBarMessage("재생시간 초과(30초)");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleDeleteSuccess = (status) => {
    if (status === 204) {
      setSnackBarMessage("삭제 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoCamCorders", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("데이터없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleUpdateSuccess = (status) => {
    if (status === 200) {
      setSnackBarMessage("수정 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoCamCorders", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("데이터없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleCloseSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  if (isLoading) {
    return <div>로딩중...</div>;
  }

  if (isError) {
    return <div>로그인을 다시 해주세요</div>;
  }

  return (
    <Box sx={{ width: "100%", height: "auto", pb: 3 }}>
      <Box
        sx={{
          width: "100%",
          height: "auto",
          mb: 1,
          mt: 0,
          mx: 0,
          px: 2,
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f5f1e7",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // 그림자 추가
        }}
      >
        <Box sx={{ width: "100%", height: "auto", mb: 2, mt: 1 }}>
          <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ThemeProvider theme={pickerThemeCam}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ko"
                >
                  <DesktopDatePicker
                    sx={{ mt: 1, width: "100%" }}
                    margin="dense"
                    format="YYYY년"
                    label="캘린더"
                    value={
                      dayjs(value).isSame(specificDate, "day")
                        ? dayjs()
                        : dayjs(value)
                    }
                    onChange={(newValue) => setValue(newValue)}
                    views={["year"]}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "100%",
                          height: "40px",
                          backgroundColor: "white", // ← 폼 배경 하얗게
                          borderRadius: "8px", // ← 둥근 모서리(선택)
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)", // ← 그림자
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root": {
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root.Mui-focused": {
                            color: "#8c6744", // 예: 강조색
                          },
                          "& .MuiSvgIcon-root path": {
                            fill: "#8c6744",
                          },
                        },
                      },
                      actionBar: {
                        actions: ["today"],
                      },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
            justifyContent="center"
            sx={{ pt: "4px" }}
          >
            <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleDetailClickOpen}
                sx={{
                  width: "100%",
                  height: "40px",
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }} // 캘린더와 동일한 높이 설정
              >
                상세검색
              </Button>
            </Grid>
            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleNewClick}
                sx={{
                  width: "100%",
                  height: "40px", // 캘린더와 동일한 높이
                  background: "linear-gradient(45deg, #8e44ad, #e84393)",
                  color: "#fff",
                  borderRadius: 1,
                  "&:hover": {
                    background: "linear-gradient(45deg, #a64fcf, #ff5ea2)",
                  },
                }}
              >
                NEW
              </Button>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={handleResetClick}
                sx={{
                  width: 40,
                  height: 40,
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  borderRadius: "50%", // 동그랗게
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Grid>

            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleToggleClick}
                sx={{
                  width: "100%",
                  height: "40px",
                  color: "white",
                  background: isLatest
                    ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 배경
                    : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 배경
                  "&:hover": {
                    background: isLatest
                      ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 호버
                      : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 호버
                  },
                }}
              >
                {isLatest ? "과거순" : "최신순"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* 동영상 리스트 */}
      <Card
        sx={{
          width: "100vw", // 화면 전체 폭
          ml: -1,
          mb: 1,
          px: 2,
          py: 1,
          // borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          backgroundColor: "#f5f4f2",
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: 1, color: "#8c6744" }}>
          {formatDateCamCorder(sortedData[0]?.date)}
        </Typography>
        <Box
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)", // 3개씩 배치
            gap: 1,
          }}
        >
          {sortedData.map((video) => (
            <Card
              key={video.id}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                cursor: "pointer",
                backgroundColor: "#f5f4f2",
              }}
              onClick={() => handleVideoClick(video)}
            >
              {/* 비디오 썸네일 */}
              <CardMedia
                component="img"
                image={`${video.thumbnail}?w=248&fit=crop&auto=format`}
                alt={video.title}
                sx={{
                  p: "3px",
                  width: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  borderRadius: 2,
                }}
              />

              {/* 제목 표시 */}
              <CardContent sx={{ pb: "4px !important", pt: 0, px: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#B8860B",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: video.title?.length > 12 ? "0.62rem" : video.title?.length > 8 ? "0.72rem" : "0.8rem",
                  }}
                >
                  {video.title}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Card>
      {tokenInfo.is_superuser && (
        <UploadButtonCam onUploadSuccess={handleUploadSuccess} />
      )}
      {selectedVideo && (
        <CamCorderDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          date={formatDateDialog(selectedVideo.date)}
          datas={selectedVideo}
          onDeleteSuccess={handleDeleteSuccess}
          onUpdateSuccess={handleUpdateSuccess}
          selectedIndex={selectedIndex}
          groupLength={selectedGroup.length}
          onPrev={handlePrev}
          onNext={handleNext}
          onJump={handleJump}
          slideDirection={slideDirection}
          animKey={animKey}
          downloadFile={downloadFile}
        />
      )}
      <MySnackbar
        open={isSnackBarOpen}
        onClose={handleCloseSnackBar}
        message={snackBarMessage}
        severity={snackBarSeverity}
      />

      {/*상세검색 다이어로그 */}
      <Dialog
        open={detailSearchOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2", // 전체 배경 색
            borderRadius: "12px", // 모서리 둥글게
          },
        }}
      >
        <DialogTitle sx={{ color: "#8c6744", bgcolor: "#f5f4f2" }}>
          단어 검색
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: "#f5f4f2",
            pt: "16px !important",
          }}
        >
          <TextField
            label="검색어 입력"
            variant="outlined"
            fullWidth
            inputRef={inputRef}
            margin="dense"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f4f2",
                "& fieldset": {
                  borderColor: "#c9b08a",
                },
                "&:hover fieldset": {
                  borderColor: "#b89263",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#8c6744",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#8c6744",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#8c6744",
              },
              /* ✨ 입력 텍스트 색상 변경 */
              "& .MuiInputBase-input": {
                color: "#8c6744", // ← 원하는 텍스트 색상
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDetailSearch}
            variant="contained"
            sx={{
              background: "#B8860B", // 민트 → 초록
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520", // hover 버전
              },
            }}
          >
            검색
          </Button>
          <Button
            onClick={handleDetailClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Camcorder;
